import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import UsermanMenu from "../components/UsermanMenu";
import api from "../services/api";

const UsermanRouter = () => {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [editingRouter, setEditingRouter] = useState(null);
  const [routerToDelete, setRouterToDelete] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [formData, setFormData] = useState({
    admin_profile_id: "",
    name: "",
    shared_secret: "",
    address: "",
    coa_port: "3799",
    disabled: false,
  });

  const showToast = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(""), 3000);
  };

  // === GET Routers ===
  const fetchRouters = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `https://api.wifikamu.my.id/userman-router-get/${usermanId}`
      );
      const data = await res.json();
      if (data.success) {
        setRouters(data.data);
      } else {
        showToast("Gagal mengambil data router", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Terjadi kesalahan saat fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (usermanId) fetchRouters();
  }, [usermanId]);

  // === Input Change ===
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // === Tambah ===
  const openAddModal = () => {
    setEditingRouter(null);
    setFormData({
      admin_profile_id: usermanId,
      name: "",
      shared_secret: "",
      address: "",
      coa_port: "3799",
      disabled: false,
    });
    setIsModalOpen(true);
  };

  // === Edit ===
  const handleEdit = (router) => {
    setEditingRouter(router);
    setFormData({
      admin_profile_id: usermanId,
      name: router.name,
      shared_secret: router.shared_secret,
      address: router.address,
      coa_port: router.coa_port,
      disabled: router.disabled === 1 ? true : false,
    });
    setIsModalOpen(true);
  };

  // === Delete ===
  const handleDelete = (router) => {
    setRouterToDelete(router);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        "https://api.wifikamu.my.id/userman-router-delete",
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mikrotik_id: routerToDelete.mikrotik_id,
            admin_profile_id: usermanId,
          }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setRouters((prev) =>
          prev.filter((r) => r.mikrotik_id !== routerToDelete.mikrotik_id)
        );
        showToast("Router berhasil dihapus", "success");
      } else {
        showToast("Gagal menghapus router", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Terjadi kesalahan saat hapus router", "error");
    } finally {
      setIsConfirmModalOpen(false);
      setRouterToDelete(null);
      setLoading(false);
    }
  };

  // === Submit Add / Edit ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsModalOpen(false);

    try {
      if (editingRouter) {
        // Update
        const res = await fetch(
          "https://api.wifikamu.my.id/userman-router-update",
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: editingRouter.id,
              mikrotik_id: editingRouter.mikrotik_id,
              ...formData,
              disabled: formData.disabled ? 1 : 0,
              userman_id: usermanId,
            }),
          }
        );
        const data = await res.json();
        if (data.success) {
          setRouters((prev) =>
            prev.map((r) =>
              r.id === editingRouter.id
                ? { ...r, ...formData, disabled: formData.disabled ? 1 : 0 }
                : r
            )
          );
          showToast("Router berhasil diperbarui", "success");
        } else {
          showToast("Gagal update router", "error");
        }
      } else {
        // Add
        const res = await fetch(
          "https://api.wifikamu.my.id/userman-router-add",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...formData,
              userman_id: usermanId,
              disabled: formData.disabled ? 1 : 0,
            }),
          }
        );
        const data = await res.json();
        if (data.success) {
          fetchRouters();
          showToast("Router berhasil ditambahkan", "success");
        } else {
          showToast("Gagal menambah router", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Terjadi kesalahan saat simpan router", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 font-sans antialiased text-gray-800 dark:bg-gray-900 dark:text-gray-200 min-h-screen">
      {/* Toast */}
      {toastMessage && (
        <div
          className={`fixed top-4 right-4 z-[999] px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ease-in-out ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toastMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          {deviceName} /
          <UsermanMenu userman_id={usermanId} device_name={deviceName} /> /
          Router
        </h1>
        <button
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md"
          onClick={openAddModal}
        >
          Tambah Router
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="p-3 text-left font-semibold">Nama</th>
                <th className="p-3 text-left font-semibold">Mikrotik ID</th>
                <th className="p-3 text-left font-semibold">Alamat</th>
                <th className="p-3 text-left font-semibold">Secret</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3 text-center font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {routers.map((router) => (
                <tr key={router.id} className="border-t dark:border-gray-700">
                  <td className="p-3">{router.name}</td>
                  <td className="p-3">{router.mikrotik_id}</td>
                  <td className="p-3">{router.address}</td>
                  <td className="p-3">{router.shared_secret}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        router.disabled === 0
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {router.disabled === 0 ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td className="p-3 flex justify-center gap-3">
                    <button
                      onClick={() => handleEdit(router)}
                      className="px-3 py-2 text-blue-600 hover:text-blue-800 border border-blue-600 hover:border-blue-800 rounded-md text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(router)}
                      className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-600 hover:border-red-800 rounded-md text-xs"
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-6 text-left shadow-xl">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              {editingRouter ? "Edit Router" : "Tambah Router Baru"}
            </h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
              <input
                type="text"
                name="name"
                placeholder="Nama Router"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg text-black dark:text-white"
              />
              <input
                type="text"
                name="address"
                placeholder="Alamat IP"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg text-black dark:text-white"
              />
              <input
                type="text"
                name="shared_secret"
                placeholder="Shared Secret"
                value={formData.shared_secret}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg text-black dark:text-white"
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="disabled"
                  name="disabled"
                  checked={formData.disabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="disabled"
                  className="text-gray-900 dark:text-gray-100"
                >
                  Nonaktifkan Router
                </label>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 text-left shadow-xl">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              Konfirmasi Hapus
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-200">
              Apakah kamu yakin ingin menghapus router "{routerToDelete?.name}"?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsermanRouter;
